import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import {
  BaSeGrid,
  BaSeGridItem,
  BaSeSmall1,
  BaSeTheme,
  BaSeHeading5,
  BaSeDialogContext,
  BaSeShapeButton,
  BaSeButton,
  BaSeBreakpointContext,
} from '@base/react';
import { FlexedStyledRow, AlignEnd } from './relatorio-extrato-lista-styled';
import { BodyCard, BodyCardCell, CardHeader } from '../relatorios-card-styled';
import { ShowButtonsAction } from '../head-buttons-action';
import { ModalShare } from '../modal-share';
import { useRelatorio } from '../../../services/relatorio/RelatorioService';

const DEFAULT_TEXT_COLOR = BaSeTheme.colors.institucionais.cinzaSebrae20;

const negative = (val) => {
  return !val
    ? DEFAULT_TEXT_COLOR
    : val > 0
    ? BaSeTheme.colors.feedbackInterface.sucesso26
    : BaSeTheme.colors.feedbackInterface.erro35;
};

const minus = (val) => {
  return (' ' + val).includes('-') ? ' - ' : '';
};

const RelatorioExtratoRow = ({ dt1, dt2, dt3, dt4, ele }) => {
  const displayDate = React.useCallback(() => {
    const [y, m, d] = dt1.split('-');
    return `${d}/${m}/${y}`;
  }, [dt1]);

  const handleStatus = React.useCallback(
    (val) => {
      if (val === '0,00') return DEFAULT_TEXT_COLOR;
      return ele?.tipoLancamento === 'Despesa'
        ? BaSeTheme.colors.feedbackInterface.erro35
        : BaSeTheme.colors.feedbackInterface.sucesso26;
    },
    [ele],
  );

  const valor = Sebrae.useMask(dt3, 'decimal');
  const saldo = Sebrae.useMask(dt4, 'decimal');
  return (
    <BodyCardCell>
      <BaSeGrid>
        <BaSeGridItem size={3}>
          <BaSeSmall1>{displayDate()}</BaSeSmall1>
        </BaSeGridItem>
        <BaSeGridItem size={3}>
          <BaSeSmall1>{dt2}</BaSeSmall1>
        </BaSeGridItem>
        <BaSeGridItem size={3}>
          <AlignEnd>
            <BaSeSmall1 color={handleStatus(dt3)}>
              {ele?.tipoLancamento === 'Despesa' && '-'}
              R$
              {' ' + valor}
            </BaSeSmall1>
          </AlignEnd>
        </BaSeGridItem>
        <BaSeGridItem size={3}>
          <AlignEnd>
            <BaSeSmall1 color={negative(dt4)}>
              {minus(saldo)}
              {'R$ '}
              {saldo.replace('-', '')}
            </BaSeSmall1>
          </AlignEnd>
        </BaSeGridItem>
      </BaSeGrid>
    </BodyCardCell>
  );
};

const RelatorioExtratoFlexedRow = ({
  dt1,
  dt2,
  color,
  negative,
  end,
  isBlue,
}) => {
  const money = Sebrae.useMask(dt2, 'decimal');
  return (
    <BodyCardCell end={end} isBlue={isBlue}>
      <FlexedStyledRow>
        <BaSeSmall1
          color={BaSeTheme.colors.institucionais.cinzaSebrae20}
          isBold={true}
        >
          {dt1}
        </BaSeSmall1>
        <BaSeSmall1 color={color}>
          {' '}
          {negative && '- '} R$ {money}
        </BaSeSmall1>
      </FlexedStyledRow>
    </BodyCardCell>
  );
};

export const RelatorioExtratoLista = ({
  lancamentos,
  saldoAnterior,
  saldoFinal,
  saldoPeriodo,
  totalDespesas,
  totalReceitas,
  download,
  loadingDownload,
  paramsFilter,
  isPublic = false,
}) => {
  const { compartilharRelatorio } = useRelatorio();
  const { showModal } = React.useContext(BaSeDialogContext);
  const { inTablet } = React.useContext(BaSeBreakpointContext);

  const mapListToTable = React.useCallback((ele) => {
    return {
      ...ele,
      dt1: ele?.dataCadastro,
      dt2: ele?.descricao,
      dt3: parseFloat(ele?.valor).toFixed(2),
      dt4: parseFloat(ele?.saldo).toFixed(2),
    };
  }, []);

  const list = React.useCallback(() => {
    return (
      <>
        {lancamentos.map(mapListToTable).map((ele, id) => (
          <RelatorioExtratoRow
            key={id}
            dt1={ele.dt1}
            dt2={ele.dt2}
            dt3={ele.dt3}
            dt4={ele.dt4}
            ele={ele}
          />
        ))}
      </>
    );
  }, [lancamentos, mapListToTable]);

  const print = React.useCallback(() => window.print(), []);

  const handleShowModal = React.useCallback(
    () =>
      showModal({
        title: 'Compartilhar',
        actions: [],
        size: 'medium',
        content: ({ closeModal }) => (
          <ModalShare
            closeModal={closeModal}
            filters={paramsFilter}
            callback={compartilharRelatorio}
            tipoRelatorio={'RELATORIO_EXTRATO'}
          />
        ),
      }),
    [paramsFilter, compartilharRelatorio, showModal],
  );

  return (
    <BodyCard>
      <BodyCardCell start={1}>
        <CardHeader full>
          <BaSeHeading5
            isBold
            color={BaSeTheme.colors.institucionais.azulSebrae10}
          >
            Extrato
          </BaSeHeading5>
          <CardHeader>
            {!isPublic && (
              <ShowButtonsAction
                download={download}
                loadingDownload={loadingDownload}
                print={print}
                isDisabled={false}
                showModal={handleShowModal}
              />
            )}

            {isPublic &&
              (inTablet ? (
                <BaSeButton
                  value="Imprimir"
                  leftIcon="print"
                  onClick={print}
                  type="secondary"
                />
              ) : (
                <BaSeShapeButton
                  shape="square"
                  nameIcon="print"
                  onClick={print}
                  type="secondary"
                />
              ))}
          </CardHeader>
        </CardHeader>
      </BodyCardCell>
      <BodyCardCell>
        <BaSeGrid>
          <BaSeGridItem size={3}>
            <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold={true}>
              Data
            </BaSeSmall1>
          </BaSeGridItem>
          <BaSeGridItem size={3}>
            <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold={true}>
              Descrição
            </BaSeSmall1>
          </BaSeGridItem>
          <BaSeGridItem size={3}>
            <AlignEnd>
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold={true}>
                Valor
              </BaSeSmall1>
            </AlignEnd>
          </BaSeGridItem>
          <BaSeGridItem size={3}>
            <AlignEnd>
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold={true}>
                Saldo
              </BaSeSmall1>
            </AlignEnd>
          </BaSeGridItem>
        </BaSeGrid>
      </BodyCardCell>
      {list()}
      <RelatorioExtratoFlexedRow
        dt1="Saldo anterior"
        dt2={parseFloat(saldoAnterior).toFixed(2).replace('-', '')}
        color={negative(saldoAnterior)}
        negative={saldoAnterior < 0}
      />
      <RelatorioExtratoFlexedRow
        dt1="Total de receitas no período"
        dt2={parseFloat(totalReceitas).toFixed(2).replace('-', '')}
        color={negative(totalReceitas)}
        negative={totalReceitas < 0}
      />
      <RelatorioExtratoFlexedRow
        dt1="Total de despesas no período	"
        dt2={parseFloat(totalDespesas).toFixed(2).replace('-', '')}
        color={BaSeTheme.colors.feedbackInterface.erro35}
        negative={true}
      />
      <RelatorioExtratoFlexedRow
        dt1="Saldo do período	"
        dt2={parseFloat(saldoPeriodo).toFixed(2).replace('-', '')}
        color={negative(saldoPeriodo)}
        negative={saldoPeriodo < 0}
      />
      <RelatorioExtratoFlexedRow
        dt1="Saldo final"
        dt2={parseFloat(saldoFinal).toFixed(2).replace('-', '')}
        color={negative(saldoFinal)}
        negative={saldoFinal < 0}
        end={1}
        isBlue={true}
      />
    </BodyCard>
  );
};
